import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save, Star } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const EMPTY = { name: "", city: "", rating: 5, text: "", active: true };

export default function AdminReviews() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/reviews").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim() || !draft.text.trim()) return toast.error("Nom et texte requis");
    try { await api.post("/admin/reviews", draft); setDraft(EMPTY); load(); toast.success("Avis ajouté"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const save = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, r);
      setEditing(null); load(); toast.success("Avis mis à jour");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    try { await api.delete(`/admin/reviews/${id}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div>
      <div className="font-heading text-3xl font-black uppercase tracking-tight">Avis clients</div>
      <div className="h-1 w-16 bg-brand mt-2 mb-6" />

      <div className="bg-ink-900 border border-ink-700 p-4 mb-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Nouvel avis</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input className="input-admin" placeholder="Nom" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input className="input-admin" placeholder="Ville" value={draft.city} onChange={(e) => setDraft({ ...draft, city: e.target.value })} />
          <select className="input-admin" value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: Number(e.target.value) })}>
            {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? "s" : ""}</option>)}
          </select>
          <button onClick={create} className="bg-brand hover:bg-brand-600 text-white px-4 py-2.5 sm:py-0 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-sm transition-colors">
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <textarea className="input-admin mt-2 h-20 py-2" placeholder="Témoignage" value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />
      </div>

      <div className="grid gap-3">
        {rows.map((r) => (
          <div key={r.id} className="bg-ink-900 border border-ink-700 p-4">
            {editing === r.id ? (
              <EditRow row={r} onSave={save} onCancel={() => setEditing(null)} />
            ) : (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold">{r.name}</div>
                    {r.city && <div className="text-zinc-400 text-sm">— {r.city}</div>}
                    <div className="flex items-center gap-0.5 text-brand">
                      {[...Array(r.rating || 0)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                    </div>
                    {!r.active && <span className="text-xs text-red-400 font-bold">MASQUÉ</span>}
                  </div>
                  <div className="text-sm text-zinc-200 mt-1">{r.text}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEditing(r.id)} className="text-zinc-400 hover:text-brand text-xs font-bold uppercase">Modifier</button>
                  <button onClick={() => remove(r.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <div className="text-zinc-500 text-center py-8">Aucun avis</div>}
      </div>

      <style>{`.input-admin{ width:100%; height:40px; padding:0 12px; background:#3C3C40; border:1px solid #4A4A4D; color:#fff; font-size:14px; outline:none; } .input-admin:focus{ border-color:#FF6200;}`}</style>
    </div>
  );
}

function EditRow({ row, onSave, onCancel }) {
  const [r, setR] = useState(row);
  return (
    <div>
      <div className="grid sm:grid-cols-4 gap-2">
        <input className="input-admin" value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })} />
        <input className="input-admin" value={r.city || ""} onChange={(e) => setR({ ...r, city: e.target.value })} />
        <select className="input-admin" value={r.rating || 5} onChange={(e) => setR({ ...r, rating: Number(e.target.value) })}>
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}★</option>)}
        </select>
        <label className="text-sm text-zinc-300 inline-flex items-center gap-2"><input type="checkbox" checked={!!r.active} onChange={(e) => setR({ ...r, active: e.target.checked })} /> Actif</label>
      </div>
      <textarea className="input-admin mt-2 h-20 py-2" value={r.text} onChange={(e) => setR({ ...r, text: e.target.value })} />
      <div className="mt-2 flex gap-2">
        <button onClick={() => onSave(r)} className="bg-brand hover:bg-brand-600 text-white px-4 py-2 text-sm font-bold uppercase inline-flex items-center gap-2"><Save size={14} /> Enregistrer</button>
        <button onClick={onCancel} className="bg-ink-800 text-white px-4 py-2 text-sm font-bold uppercase">Annuler</button>
      </div>
    </div>
  );
}
